import React, { useState } from "react";
import { Button } from "@/components/ui-kit";
import api, { apiErr } from "@/lib/api";
import { track } from "@/lib/track";
import { GRADE_OPTIONS, gradeLabel } from "@/config";
import { Mail, CheckCircle2 } from "lucide-react";

// Inline double-opt-in lead capture form. Used by the landing "Free resources"
// section and the exit-intent popup. On submit → POST /leads (sends a
// confirmation email); shows a "check your inbox" success state.
export default function LeadCaptureForm({ source = "unknown", magnet = "math_readiness_checklist", compact = false }) {
  const [email, setEmail] = useState("");
  const [grade, setGrade] = useState(5);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    setLoading(true);
    track("lead_form_submitted", { source, magnet });
    try {
      const { data } = await api.post("/leads", { email, grade: Number(grade), source, magnet });
      try { localStorage.setItem("ns_lead_submitted", "1"); } catch { /* noop */ }
      setDone(true);
      if (data.status === "already_confirmed") {
        track("lead_already_confirmed", { source });
      }
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail) || "Couldn't submit — please try again.";
      setDone(false);
      setEmail("");
      alert(msg);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="flex items-start gap-3 rounded-[var(--ns-radius)] bg-[var(--ns-primary-light)] p-4" data-testid="lead-success">
        <CheckCircle2 size={20} className="text-[var(--ns-primary)] mt-0.5 shrink-0" />
        <div className="text-sm text-[var(--ns-text)]">
          <b>Almost there!</b> Check your inbox and tap "Confirm" — your checklist is on its way, plus a short 5-day series.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={compact ? "grid gap-2" : "grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"} data-testid="lead-form">
      <div className={compact ? "grid gap-2" : "grid grid-cols-[1fr_120px] gap-2"}>
        <div>
          {!compact && <label className="text-xs font-semibold text-[var(--ns-subtle)]">Your email</label>}
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ns-subtle)]" />
            <input data-testid="lead-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] pl-9 pr-3 py-3 focus:outline-none focus:border-[var(--ns-primary)]"
              style={{ minHeight: "var(--ns-touch)" }} />
          </div>
        </div>
        <div>
          {!compact && <label className="text-xs font-semibold text-[var(--ns-subtle)]">Grade</label>}
          <select data-testid="lead-grade" value={grade} onChange={(e) => setGrade(e.target.value)}
            className="w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-2 py-3 focus:outline-none focus:border-[var(--ns-primary)]"
            style={{ minHeight: "var(--ns-touch)" }}>
            {GRADE_OPTIONS.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
          </select>
        </div>
      </div>
      <Button type="submit" data-testid="lead-submit" disabled={loading} className={compact ? "w-full" : ""}>
        {loading ? "Sending…" : "Send my checklist"}
      </Button>
    </form>
  );
}
